import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { makeCustomEvent, EVENT } from '../../../util/events';
import { consume } from '@lit/context';
import { formDataContext } from '../../../contexts/formData';
import { SEVERITY, RateQuoteFormData, isValidForm } from '../../../util/form';
import '../../../components/continue-button';
import '../../../components/back-button';
import { breakpointContext } from '../../../contexts/breakpoint';
import { BREAKPOINT } from '../../../util/breakpoints';
import { classMap } from 'lit/directives/class-map.js';
import { SOURCE } from '../../../util/source';
import { sourceContext } from '../../../contexts/source';

@customElement('rq-step-estimate')
export class RateQuoteEstimate extends LitElement {
    static styles = css`
        .wrapper {
            display: flex;
            flex-direction: column;
            gap: 24px;
        }
        .skip-btn {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 4px;
        }
        .skip-icon {
            display: flex;
            justify-content: start;
        }
        .icon-padding {
            padding-top: 1px;
        }
    `;

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('lh-change', this.onClickHandler as EventListener);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('lh-change', this.onClickHandler as EventListener);
    }

    @consume({ context: formDataContext, subscribe: true })
    data!: RateQuoteFormData;

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @consume({ context: sourceContext })
    source!: SOURCE;

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    private onClickHandler({
        detail: { checked, name, value },
    }: CustomEvent<{ checked: boolean; name: keyof RateQuoteFormData; value: SEVERITY }>) {
        const updatedForm: RateQuoteFormData = { ...this.data, [name]: value };
        if (!checked) {
            delete updatedForm[name];
        }
        this.dispatchEvent(makeCustomEvent(EVENT.UPDATE_FORM, { data: updatedForm }));
    }

    get disableContinue() {
        return this.data.severity === undefined || !isValidForm(this.data);
    }

    get disableSkip() {
        return !isValidForm(this.data);
    }

    render() {
        return html`
            <div class="wrapper">
                <lh-typography type="heading" selector="h2" size=${this.isMobile ? 'xxl' : 'lg'}>
                    Let us help you pick the right plan
                </lh-typography>
                <lh-typography>
                    We'll use this to make better recommendations, but we'll still show you every available option so
                    you can make the best choice.
                </lh-typography>
                <lh-typography>
                    <strong>What best describes your health care needs?</strong>
                </lh-typography>
                <lh-checkbox-card
                    layout="horizontal"
                    name="severity"
                    value=${SEVERITY.MINIMAL}
                    default-checked=${this.data.severity === SEVERITY.MINIMAL ? true : nothing}
                >
                    <div slot="content">
                        <lh-typography><strong>Minimal</strong></lh-typography>
                        <lh-typography
                            >You visit the doctor once per year and have no or very few prescription medications.
                        </lh-typography>
                    </div>
                </lh-checkbox-card>
                <lh-checkbox-card
                    layout="horizontal"
                    name="severity"
                    value=${SEVERITY.MODERATE}
                    default-checked=${this.data.severity === SEVERITY.MODERATE ? true : nothing}
                >
                    <div slot="content">
                        <lh-typography><strong>Moderate</strong></lh-typography>
                        <lh-typography
                            >You visit the doctor a few times a year, have a couple prescriptions, and get regular lab
                            tests.
                        </lh-typography>
                    </div>
                </lh-checkbox-card>
                <lh-checkbox-card
                    layout="horizontal"
                    name="severity"
                    value=${SEVERITY.SIGNIFICANT}
                    default-checked=${this.data.severity === SEVERITY.SIGNIFICANT ? true : nothing}
                >
                    <div slot="content">
                        <lh-typography><strong>Significant</strong></lh-typography>
                        <lh-typography
                            >You visit the doctor frequently, have several prescriptions, get frequent labs, have a
                            chronic condition, or anticipate hospital visits.
                        </lh-typography>
                    </div>
                </lh-checkbox-card>
                <rq-continue-button
                    .disabled=${this.disableContinue}
                    analytics-id="rate-quote:${this.source}:u65:step4:continue:button-filled"
                    submit
                ></rq-continue-button>
                <lh-button
                    analytics-id="rate-quote:${this.source}:u65:step4:skip:button-outlined"
                    full-width
                    @click=${() => {
                        const updatedForm: RateQuoteFormData = {
                            ...this.data,
                        };
                        delete updatedForm.severity;
                        this.dispatchEvent(makeCustomEvent(EVENT.UPDATE_FORM, { data: updatedForm }));
                        this.dispatchEvent(makeCustomEvent(EVENT.SUBMIT));
                    }}
                    variant="outlined"
                >
                    <div class="skip-btn">
                        <div></div>
                        <div>Skip</div>
                        <div class=${classMap({ 'skip-icon': true, 'icon-padding': !this.isMobile })}>
                            <lh-icon size="sm" name="caret-right"></lh-icon>
                        </div>
                    </div>
                </lh-button>
                <rq-back-button analytics-id="rate-quote:${this.source}:u65:step4:back:button-text"></rq-back-button>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-step-estimate': RateQuoteEstimate;
    }
}
